import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import CopyIdButton from "../components/CopyIdButton";
import PageLoader from "../components/PageLoader";
import AlertModal from "../components/AlertModal";
import TechnicModal from "../components/TechnicModal";
import OrderCostsModal from "../components/OrderCostsModal";
import ImageManager from "../components/ImageManager";
import FirmPickerField from "../components/FirmPicker";
import {
  firmService,
  modelService,
  orderService,
  reportService,
  settingsService,
} from "../services/dataService";
import {
  CustomerRevenueDto,
  Firm,
  Model,
  ModelCost,
  MonthlyProfitDto,
  MonthlyQuantityDto,
  MonthlyRevenueDto,
  OrderWithCostReportDto,
  ReportSummaryDto,
  Settings,
  Technic,
} from "../types";
import { formatCurrency, formatNumber, getImageUrl } from "../utils/formatters";
import {
  TECHNIC_MATCH_MODES,
  TechnicMatchMode,
  getTechnicMatchHint,
  matchesTechnicFilter,
} from "../utils/technicMatch";
import { todayLocal } from "../utils/dates";
import { useExchangeRates } from "../context/ExchangeRateContext";
import { usePermissions } from "../context/PermissionContext";
import "./Reports.css";

/* ============================================================================
   Mali Raporlar (fr-*)
   Sipariş tablosu (sunucu sayfalı + istemci süzgeçleri) ve dört grafik sekmesi.
   Para alanları TL'ye indirgenir; birim fiyat orijinal dövizle de gösterilir.
   ========================================================================== */

type ReportTab = "table" | "monthly-quantity" | "monthly-revenue" | "customer-revenue" | "monthly-profit";
type ProfitStatus = "above" | "below" | "loss";
type SortDir = "asc" | "desc";
type AlertKind = "info" | "success" | "warning" | "error";

/** Tablo satırı — sunucu DTO'sunun TL'ye indirgenmiş hâli. unitPrice orijinal döviz, diğer para alanları TL. */
interface ReportRow {
  orderId: string;
  modelId: string;
  modelCode: string;
  modelName: string;
  firmCode: string;
  firmName: string;
  quantity: number;
  technics: string[];
  paintCount: number;
  costItems: ModelCost[];
  totalAmount: number;
  totalCost: number;
  costWithOverhead: number;
  finalCostWithProfit: number;
  unitPrice: number;
  unitPriceTRY: number;
  unitCost: number;
  suggestedPrice: number;
  currency: string;
  acceptanceDate: string;
  completionDate: string;
}

const PAGE_SIZE = 50;
const MONTH_NAMES = ["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran", "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"];

/** Kategorik seri renkleri — doğrulanmış palet, sabit sıra. Renk varlığı izler (ciro hep s1 vb.), sırayı değil. */
const S = { s1: "#2B61C2", s2: "#D97706", s3: "#0F9B8E", s4: "#7C3AED" } as const;
const CH = { grid: "#EDF1F7", axis: "#DCE4EF", tick: "#64748B", cursor: "rgba(27,42,74,.06)" } as const;
const TICK = { fontSize: 11, fill: CH.tick };
const DOT = (c: string) => ({ r: 4, fill: "#fff", stroke: c, strokeWidth: 2 });

const compact = new Intl.NumberFormat("tr-TR", { notation: "compact", maximumFractionDigits: 1 });
const fmtAxis = (v: unknown) => compact.format(Number(v) || 0);
const fmtInt = (v: number) => formatNumber(v, 0);
const fmtTL = (v: number) => formatCurrency(v, "TRY");
const fmtPct = (v: number, d = 1) => `%${v.toFixed(d)}`;
const fmtDate = (iso?: string) => (iso ? new Date(iso).toLocaleDateString("tr-TR") : "-");
const isTRY = (c?: string) => !c || c === "TRY" || c === "TL";
const trunc = (s: string, n = 20) => (s.length > n ? `${s.slice(0, n - 1)}…` : s);
const STATUS_LABEL: Record<ProfitStatus, string> = { above: "Hedef üstü", below: "Hedef altı", loss: "Zarar" };

/** Maliyet kaleminin bir tutarını, kalemde saklı o günkü kurla TL'ye çevirir. */
const costToTRY = (amount: number | undefined, cost: ModelCost): number => {
  const a = amount || 0;
  if (isTRY(cost.currency)) return a;
  if (cost.currency === "USD" && cost.usdRate) return a * cost.usdRate;
  if (cost.currency === "EUR" && cost.eurRate) return a * cost.eurRate;
  if (cost.currency === "GBP" && cost.gbpRate) return a * cost.gbpRate;
  return 0;
};

const cmp = (a: unknown, b: unknown) =>
  typeof a === "number" && typeof b === "number" ? a - b : String(a ?? "").localeCompare(String(b ?? ""), "tr");

/* ---------- İkonlar ---------- */
const svg = (d: React.ReactNode, extra?: React.SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...extra}>{d}</svg>
);
const I = {
  table: svg(<><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M3 9h18M3 15h18M9 3v18" /></>),
  bars: svg(<><path d="M5 20V12M12 20V4M19 20v-7" /></>),
  trend: svg(<><path d="M3 17l6-6 4 4 8-8" /><path d="M14 7h7v7" /></>),
  users: svg(<><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" /></>),
  coin: svg(<><path d="M12 1v22M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" /></>),
  eye: svg(<><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></>),
  download: svg(<><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" /></>),
  layers: svg(<><path d="M12 2 2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" /></>),
  x: svg(<path d="M18 6 6 18M6 6l12 12" />),
  image: svg(<><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><path d="m21 15-5-5L5 21" /></>),
  search: svg(<><circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" /></>),
  chevron: svg(<path d="m6 9 6 6 6-6" />),
  filterOff: svg(<><path d="M3 4h18l-7 8v6l-4 2v-8L3 4z" /><path d="M3 3l18 18" /></>),
  workshop: svg(<><rect x="2" y="7" width="20" height="14" rx="2" /><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" /></>),
  info: svg(<><circle cx="12" cy="12" r="10" /><path d="M12 16v-4M12 8h.01" /></>),
  check: svg(<path d="m5 12 5 5L20 7" />),
};

const TABS: { key: ReportTab; label: string; icon: React.ReactNode }[] = [
  { key: "table", label: "Sipariş Tablosu", icon: I.table },
  { key: "monthly-quantity", label: "Aylık Adet", icon: I.bars },
  { key: "monthly-revenue", label: "Aylık Ciro", icon: I.trend },
  { key: "customer-revenue", label: "Müşteri Ciro", icon: I.users },
  { key: "monthly-profit", label: "Aylık Kâr", icon: I.coin },
];

/* ---------- Küçük sunum bileşenleri ---------- */
const Kpi: React.FC<{ tone?: "navy" | "soft" | "ok" | "err"; label: string; value: React.ReactNode; sub?: React.ReactNode }> = ({ tone = "soft", label, value, sub }) => (
  <div className={`fr-kpi ${tone}`}>
    <span className="l">{label}</span>
    <span className="v">{value}</span>
    {sub && <span className="s">{sub}</span>}
  </div>
);

interface LegendItem { name: string; color: string }
const FrLegend: React.FC<{ items: LegendItem[] }> = ({ items }) => (
  <ul className="fr-legend" aria-label="Seriler">
    {items.map((it) => (
      <li key={it.name}><i style={{ background: it.color }} />{it.name}</li>
    ))}
  </ul>
);

const Tile: React.FC<{ title: string; sub?: string; legend?: LegendItem[]; className?: string; children: React.ReactNode }> = ({ title, sub, legend, className = "", children }) => (
  <section className={`fr-tile ${className}`}>
    <header className="fr-tile-head">
      <div><h3>{title}</h3>{sub && <p>{sub}</p>}</div>
      {legend && legend.length > 1 && <FrLegend items={legend} />}
    </header>
    <div className="fr-tile-body">{children}</div>
  </section>
);

/** Recharts araç ipucu — değerler metin renginde, seri kimliği yanındaki noktayla. */
const FrTip: React.FC<{ active?: boolean; payload?: any[]; label?: React.ReactNode; fmt?: (v: number, key: string) => string }> = ({ active, payload, label, fmt }) => {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div className="fr-tip" role="status">
      {label !== undefined && <div className="fr-tip-h">{label}</div>}
      {payload.map((p) => (
        <div key={String(p.dataKey)} className="fr-tip-r">
          <i style={{ background: p.color || p.fill }} />
          <span>{p.name}</span>
          <b>{fmt ? fmt(Number(p.value), String(p.dataKey)) : formatNumber(Number(p.value))}</b>
        </div>
      ))}
    </div>
  );
};

const SortGlyph: React.FC<{ active: boolean; dir: SortDir }> = ({ active, dir }) => (
  <span className={`fr-sort ${active ? "on" : ""}`} aria-hidden="true">
    {active ? (dir === "asc" ? "↑" : "↓") : "↕"}
  </span>
);

/* ============================================================================ */

const Reports: React.FC = () => {
  const { has } = usePermissions();
  const { usdRate, eurRate, gbpRate } = useExchangeRates();

  // Referans veriler
  const [models, setModels] = useState<Model[]>([]);
  const [firms, setFirms] = useState<Firm[]>([]);
  const firmsRef = useRef<Firm[]>([]);
  const [settings, setSettings] = useState<Settings | null>(null);
  const bootedRef = useRef(false);
  const [booted, setBooted] = useState(false);

  const [activeTab, setActiveTab] = useState<ReportTab>("table");

  // Sunucu süzgeçleri (değişince sayfa 1'den yeniden yüklenir)
  const [selectedFirmId, setSelectedFirmId] = useState("all");
  const [selectedModelId, setSelectedModelId] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // İstemci süzgeçleri (yüklü satırlar üzerinde)
  const [selectedTechnics, setSelectedTechnics] = useState<Technic[]>([]);
  const [technicMatchMode, setTechnicMatchMode] = useState<TechnicMatchMode>("exact");
  const [showTechnicModal, setShowTechnicModal] = useState(false);
  const [paintCountMin, setPaintCountMin] = useState("");
  const [paintCountMax, setPaintCountMax] = useState("");
  const [profitStatusFilter, setProfitStatusFilter] = useState<"all" | ProfitStatus>("all");

  // Sıralama
  const [sortField, setSortField] = useState<string>("completionDate");
  const [sortDirection, setSortDirection] = useState<SortDir>("desc");

  // Veri ve sayfalama
  const [rows, setRows] = useState<ReportRow[]>([]);
  const [summary, setSummary] = useState<ReportSummaryDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isLoadingAll, setIsLoadingAll] = useState(false);
  const pageRef = useRef(1);
  const reqRef = useRef(0); // geç gelen eski yanıtı ele
  const [expandedTechnics, setExpandedTechnics] = useState<Set<string>>(new Set());

  // Model seçici
  const [modelOpen, setModelOpen] = useState(false);
  const [modelSearch, setModelSearch] = useState("");
  const comboRef = useRef<HTMLDivElement>(null);

  // Görseller
  const [thumbs, setThumbs] = useState<Record<string, string | null>>({});
  const thumbsRequested = useRef<Set<string>>(new Set());
  const [imageOrderId, setImageOrderId] = useState<string | null>(null);

  // Pencereler
  const [detailRow, setDetailRow] = useState<ReportRow | null>(null);
  const [orderCosts, setOrderCosts] = useState<{ orderId: string; modelId: string } | null>(null);
  const [alert, setAlert] = useState<{ isOpen: boolean; message: string; type: AlertKind }>({ isOpen: false, message: "", type: "info" });
  const notify = (message: string, type: AlertKind = "info") => setAlert({ isOpen: true, message, type });

  // Grafikler
  const [chartYear, setChartYear] = useState(new Date().getFullYear());
  const [chartLoading, setChartLoading] = useState(false);
  const [monthlyQuantity, setMonthlyQuantity] = useState<MonthlyQuantityDto[]>([]);
  const [monthlyRevenue, setMonthlyRevenue] = useState<MonthlyRevenueDto[]>([]);
  const [customerRevenue, setCustomerRevenue] = useState<CustomerRevenueDto[]>([]);
  const [monthlyProfit, setMonthlyProfit] = useState<MonthlyProfitDto[]>([]);
  const [custYear, setCustYear] = useState<number | "">(new Date().getFullYear());
  const [custMinRevenue, setCustMinRevenue] = useState("");
  const [custMaxRevenue, setCustMaxRevenue] = useState("");
  const [revenueConvertTRY, setRevenueConvertTRY] = useState(false);
  const [custTopN, setCustTopN] = useState(0);
  const [chartSortKey, setChartSortKey] = useState("");
  const [chartSortDir, setChartSortDir] = useState<SortDir>("desc");

  const profitMargin = settings?.profitMargin || 40;
  const overheadRate = settings?.overheadCostRate ?? 40;

  /* ---------- Kur ---------- */
  // Birim fiyat: önce maliyet kaleminde saklı o günkü kur, yoksa canlı kur
  const convertPriceToTRY = useCallback((unitPrice: number, currency: string, costItems: ModelCost[]): number => {
    if (isTRY(currency)) return unitPrice;
    const first = costItems[0];
    const saved = first ? (currency === "USD" ? first.usdRate : currency === "EUR" ? first.eurRate : currency === "GBP" ? first.gbpRate : null) : null;
    if (saved) return unitPrice * saved;
    if (currency === "USD" && usdRate) return unitPrice * usdRate;
    if (currency === "EUR" && eurRate) return unitPrice * eurRate;
    if (currency === "GBP" && gbpRate) return unitPrice * gbpRate;
    return 0;
  }, [usdRate, eurRate, gbpRate]);

  const toRow = useCallback((item: OrderWithCostReportDto): ReportRow => {
    const firm = firmsRef.current.find((f) => f.firmId === item.firmId);
    const costItems = (item.costItems || []) as unknown as ModelCost[];
    const unitPrice = item.price || 0;
    const unitPriceTRY = convertPriceToTRY(unitPrice, item.priceCurrency, costItems);
    const costWithOverhead = item.costWithOverhead || 0;
    const finalCostWithProfit = item.finalCostWithProfit || 0;
    return {
      orderId: item.orderId,
      modelId: item.modelId,
      modelCode: item.modelCode,
      modelName: item.modelName,
      firmCode: firm?.firmCode || "-",
      firmName: item.firmName,
      quantity: item.quantity,
      technics: item.technicNames || item.technics || [],
      paintCount: Number(item.paintCount ?? 0),
      costItems,
      totalAmount: unitPriceTRY * item.quantity,
      totalCost: item.totalCostTRY || 0,
      costWithOverhead,
      finalCostWithProfit,
      unitPrice,
      unitPriceTRY,
      unitCost: item.quantity > 0 && costWithOverhead > 0 ? costWithOverhead / item.quantity : 0,
      suggestedPrice: item.quantity > 0 && finalCostWithProfit > 0 ? finalCostWithProfit / item.quantity : 0,
      currency: item.priceCurrency,
      acceptanceDate: item.acceptanceDate,
      completionDate: item.completionDate || "",
    };
  }, [convertPriceToTRY]);

  /* ---------- Yükleme ---------- */
  const fetchPage = useCallback(
    (page: number) => reportService.getOrdersWithCosts(page, PAGE_SIZE, selectedFirmId, selectedModelId, startDate, endDate),
    [selectedFirmId, selectedModelId, startDate, endDate],
  );

  const appendRows = (incoming: ReportRow[]) =>
    setRows((prev) => {
      const seen = new Set(prev.map((r) => r.orderId));
      return [...prev, ...incoming.filter((r) => !seen.has(r.orderId))];
    });

  const loadData = useCallback(async () => {
    const req = ++reqRef.current;
    setLoading(true);
    try {
      if (!bootedRef.current) {
        const [m, f, s] = await Promise.all([
          modelService.getAll(),
          firmService.getFirms(),
          settingsService.getDefault().catch(() => null),
        ]);
        firmsRef.current = f;
        setModels(m); setFirms(f); setSettings(s);
      }
      const [pageRes, summaryRes] = await Promise.all([
        fetchPage(1),
        reportService.getSummary(selectedFirmId, selectedModelId, startDate, endDate),
      ]);
      if (req !== reqRef.current) return; // bu arada süzgeç değişti
      pageRef.current = 1;
      setHasMore(pageRes.items.length === PAGE_SIZE);
      setRows(pageRes.items.map((i: OrderWithCostReportDto) => toRow(i)));
      setSummary(summaryRes);
    } catch (err) {
      console.error("Rapor verileri yüklenemedi:", err);
      notify("Rapor verileri yüklenirken bir hata oluştu.", "error");
    } finally {
      if (req === reqRef.current) setLoading(false);
      bootedRef.current = true;
      setBooted(true);
    }
  }, [fetchPage, selectedFirmId, selectedModelId, startDate, endDate, toRow]);

  useEffect(() => { loadData(); }, [loadData]);

  const loadMore = useCallback(async () => {
    if (isLoadingMore || !hasMore || loading) return;
    setIsLoadingMore(true);
    try {
      const next = pageRef.current + 1;
      const res = await fetchPage(next);
      pageRef.current = next;
      setHasMore(res.items.length === PAGE_SIZE);
      appendRows(res.items.map((i: OrderWithCostReportDto) => toRow(i)));
    } catch (err) {
      console.error("Devamı yüklenemedi:", err);
    } finally {
      setIsLoadingMore(false);
    }
  }, [isLoadingMore, hasMore, loading, fetchPage, toRow]);

  // Sayfa sonuna yaklaşınca devamını getir
  useEffect(() => {
    const onScroll = () => {
      const el = document.documentElement;
      const top = window.pageYOffset || el.scrollTop;
      if (top + el.clientHeight >= el.scrollHeight - 500) loadMore();
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [loadMore]);

  const loadAllOrders = async () => {
    setIsLoadingAll(true);
    try {
      const first = await fetchPage(1);
      const totalPages = Math.ceil((first.totalCount || 0) / PAGE_SIZE);
      const rest = await Promise.all(Array.from({ length: Math.max(0, totalPages - 1) }, (_, i) => fetchPage(i + 2)));
      const all = [first, ...rest].flatMap((r) => r.items as OrderWithCostReportDto[]);
      setRows(all.map((i) => toRow(i)));
      pageRef.current = totalPages;
      setHasMore(false);
      notify(`${all.length} sipariş yüklendi.`, "success");
    } catch (err) {
      console.error("Tüm siparişler yüklenemedi:", err);
      notify("Tüm siparişler yüklenirken bir hata oluştu.", "error");
    } finally {
      setIsLoadingAll(false);
    }
  };

  // Küçük görseller: yalnız daha önce istenmemiş siparişler, 10'lu gruplarla
  useEffect(() => {
    const ids = rows.map((r) => r.orderId).filter((id) => !thumbsRequested.current.has(id));
    if (ids.length === 0) return;
    ids.forEach((id) => thumbsRequested.current.add(id));
    let cancelled = false;
    (async () => {
      for (let i = 0; i < ids.length && !cancelled; i += 10) {
        const batch = ids.slice(i, i + 10);
        const results = await Promise.allSettled(batch.map(async (orderId) => {
          const images = await orderService.getImages(orderId);
          return { orderId, url: images.length > 0 ? images[0].imageUrl : null };
        }));
        const next: Record<string, string | null> = {};
        results.forEach((r) => { if (r.status === "fulfilled") next[r.value.orderId] = r.value.url; });
        if (!cancelled) setThumbs((prev) => ({ ...prev, ...next }));
      }
    })();
    return () => { cancelled = true; };
  }, [rows]);

  // Model seçici dışına tıklanınca kapanır
  useEffect(() => {
    if (!modelOpen) return;
    const onDown = (e: MouseEvent) => { if (comboRef.current && !comboRef.current.contains(e.target as Node)) setModelOpen(false); };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [modelOpen]);

  // Esc: önce pencere, sonra seçici
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (detailRow) setDetailRow(null);
      else if (modelOpen) setModelOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [detailRow, modelOpen]);

  /* ---------- Kâr yardımcıları ---------- */
  const profitOf = (r: ReportRow) => r.totalAmount - r.costWithOverhead;
  const profitRate = (r: ReportRow) => (r.costWithOverhead > 0 ? (profitOf(r) / r.costWithOverhead) * 100 : 0);
  const profitStatus = (r: ReportRow): ProfitStatus => {
    if (profitOf(r) < 0) return "loss";
    return profitRate(r) < profitMargin ? "below" : "above";
  };

  /* ---------- Süzme + sıralama ---------- */
  const paintMin = paintCountMin.trim() === "" ? null : Number(paintCountMin);
  const paintMax = paintCountMax.trim() === "" ? null : Number(paintCountMax);
  const hasFrontendFilters = selectedTechnics.length > 0 || profitStatusFilter !== "all" || paintMin !== null || paintMax !== null;
  const activeFilterCount =
    (selectedFirmId !== "all" ? 1 : 0) + (selectedModelId !== "all" ? 1 : 0) + (startDate ? 1 : 0) + (endDate ? 1 : 0) +
    (selectedTechnics.length > 0 ? 1 : 0) + (paintMin !== null || paintMax !== null ? 1 : 0) + (profitStatusFilter !== "all" ? 1 : 0);

  const filteredRows = useMemo(() => {
    let list = rows;
    if (selectedTechnics.length > 0) {
      const names = selectedTechnics.map((t) => t.name);
      list = list.filter((r) => matchesTechnicFilter(r.technics, names, technicMatchMode));
    }
    if (paintMin !== null && !Number.isNaN(paintMin)) list = list.filter((r) => r.paintCount >= paintMin);
    if (paintMax !== null && !Number.isNaN(paintMax)) list = list.filter((r) => r.paintCount <= paintMax);
    if (profitStatusFilter !== "all") list = list.filter((r) => profitStatus(r) === profitStatusFilter);
    const dir = sortDirection === "asc" ? 1 : -1;
    return [...list].sort((a, b) => {
      const av = sortField === "profit" ? profitOf(a) : (a as any)[sortField];
      const bv = sortField === "profit" ? profitOf(b) : (b as any)[sortField];
      return cmp(av, bv) * dir;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, selectedTechnics, technicMatchMode, paintMin, paintMax, profitStatusFilter, sortField, sortDirection, profitMargin]);

  // Toplamlar: istemci süzgeci yoksa sunucu özeti (tüm sayfalar), varsa süzülmüş satırlar
  const totals = useMemo(() => {
    const sum = (k: keyof ReportRow) => filteredRows.reduce((s, r) => s + (r[k] as number), 0);
    const t = hasFrontendFilters
      ? { quantity: sum("quantity"), amount: sum("totalAmount"), cost: sum("totalCost"), costWithOverhead: sum("costWithOverhead"), finalCost: sum("finalCostWithProfit") }
      : { quantity: summary?.totalQuantity || 0, amount: summary?.totalAmount || 0, cost: summary?.totalCost || 0, costWithOverhead: summary?.totalCostWithOverhead || 0, finalCost: summary?.totalFinalCost || 0 };
    const profit = t.amount - t.costWithOverhead;
    const rate = t.costWithOverhead > 0 ? (profit / t.costWithOverhead) * 100 : 0;
    return { ...t, profit, rate };
  }, [filteredRows, hasFrontendFilters, summary]);

  const handleSort = (field: string) => {
    if (sortField === field) setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortField(field); setSortDirection("asc"); }
  };

  const clearFilters = () => {
    setSelectedFirmId("all"); setSelectedModelId("all"); setStartDate(""); setEndDate("");
    setProfitStatusFilter("all"); setPaintCountMin(""); setPaintCountMax("");
    setSelectedTechnics([]); setTechnicMatchMode("exact");
  };

  const toggleTechnic = (t: Technic) =>
    setSelectedTechnics((prev) => (prev.some((x) => x.technicId === t.technicId) ? prev.filter((x) => x.technicId !== t.technicId) : [...prev, t]));

  const selectedModel = selectedModelId === "all" ? null : models.find((m) => m.modelId === selectedModelId) || null;
  const visibleModels = useMemo(() => {
    const q = modelSearch.trim().toLowerCase();
    return models
      .filter((m) => !q || m.modelCode.toLowerCase().includes(q) || (m.modelName || "").toLowerCase().includes(q))
      .sort((a, b) => a.modelCode.localeCompare(b.modelCode, "tr"))
      .slice(0, 200);
  }, [models, modelSearch]);
  const pickModel = (id: string) => { setSelectedModelId(id); setModelOpen(false); setModelSearch(""); };

  /* ---------- Excel ---------- */
  const handleExportExcel = () => {
    try {
      const data: any[] = filteredRows.map((row, index) => {
        const profit = profitOf(row);
        return {
          Sıra: index + 1,
          Firma: row.firmName,
          "Model Kodu": row.modelCode,
          Adet: row.quantity,
          Teknikler: row.technics.join(", ") || "-",
          "Boya Adedi": row.paintCount,
          "Toplam Tutar (TL)": row.totalAmount,
          "Maliyet (TL)": row.totalCost,
          "Genel Gider Dahil (TL)": row.costWithOverhead,
          "Hesaplanan Tutar (TL)": row.finalCostWithProfit,
          "Birim Fiyat": row.unitPrice,
          Döviz: row.currency,
          "Birim Fiyat (TL)": row.unitPriceTRY,
          "Birim Maliyet (TL)": row.unitCost,
          "Hesaplanan Fiyat (TL)": row.suggestedPrice,
          "Kar/Zarar (TL)": profit,
          "Kar Oranı (%)": row.totalAmount > 0 ? (profit / row.totalAmount) * 100 : 0,
          "Kar Durumu": STATUS_LABEL[profitStatus(row)],
          "Kabul Tarihi": fmtDate(row.acceptanceDate),
          "Tamamlanma Tarihi": fmtDate(row.completionDate),
        };
      });
      data.push({});
      data.push({
        Sıra: "TOPLAM", Firma: "", "Model Kodu": "", Adet: totals.quantity, Teknikler: "", "Boya Adedi": "",
        "Toplam Tutar (TL)": totals.amount, "Maliyet (TL)": totals.cost, "Genel Gider Dahil (TL)": totals.costWithOverhead,
        "Hesaplanan Tutar (TL)": totals.finalCost, "Birim Fiyat": "-", Döviz: "", "Birim Fiyat (TL)": "-", "Birim Maliyet (TL)": "-",
        "Hesaplanan Fiyat (TL)": "-", "Kar/Zarar (TL)": totals.profit, "Kar Oranı (%)": totals.rate, "Kar Durumu": "", "Kabul Tarihi": "", "Tamamlanma Tarihi": "",
      });
      const ws = XLSX.utils.json_to_sheet(data);
      ws["!cols"] = [6, 25, 15, 10, 30, 12, 18, 15, 20, 20, 15, 8, 18, 18, 20, 18, 15, 15, 15, 18].map((wch) => ({ wch }));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Rapor");
      XLSX.writeFile(wb, `Siparis_Raporu_${todayLocal()}.xlsx`);
      notify("Excel dosyası indirildi.", "success");
    } catch (err) {
      console.error("Excel dışa aktarma hatası:", err);
      notify("Excel dosyası oluşturulurken bir hata oluştu.", "error");
    }
  };

  /* ---------- Grafik verileri ---------- */
  const withMonthNames = <T extends { month: number }>(data: T[]) =>
    [...data].sort((a, b) => a.month - b.month).map((d) => ({ ...d, monthName: MONTH_NAMES[d.month - 1] || `Ay ${d.month}` }));

  useEffect(() => {
    if (activeTab === "table") return;
    let cancelled = false;
    (async () => {
      setChartLoading(true);
      try {
        const firmParam = selectedFirmId === "all" ? undefined : selectedFirmId;
        if (activeTab === "monthly-quantity") {
          const d = await reportService.getMonthlyQuantity(chartYear, firmParam);
          if (!cancelled) setMonthlyQuantity(withMonthNames(d));
        } else if (activeTab === "monthly-revenue") {
          const d = await reportService.getMonthlyRevenue(chartYear, firmParam);
          if (!cancelled) setMonthlyRevenue(withMonthNames(d));
        } else if (activeTab === "customer-revenue") {
          let sd = startDate || undefined;
          let ed = endDate || undefined;
          if (custYear && !startDate && !endDate) { sd = `${custYear}-01-01`; ed = `${custYear}-12-31`; }
          const d = await reportService.getCustomerRevenue(sd, ed);
          if (!cancelled) setCustomerRevenue(d);
        } else if (activeTab === "monthly-profit") {
          const d = await reportService.getMonthlyProfit(chartYear, firmParam);
          if (!cancelled) setMonthlyProfit(withMonthNames(d));
        }
      } catch (err) {
        console.error("Grafik verisi yüklenemedi:", err);
      } finally {
        if (!cancelled) setChartLoading(false);
      }
    })();
    setChartSortKey(""); setChartSortDir("desc");
    return () => { cancelled = true; };
  }, [activeTab, chartYear, selectedFirmId, startDate, endDate, custYear]);

  const availableYears = Array.from({ length: new Date().getFullYear() - 2019 }, (_, i) => 2020 + i).reverse();

  const handleChartSort = (key: string) => {
    if (chartSortKey === key) setChartSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setChartSortKey(key); setChartSortDir("desc"); }
  };
  const sortedData = <T extends Record<string, any>>(data: T[]): T[] =>
    chartSortKey ? [...data].sort((a, b) => cmp(a[chartSortKey], b[chartSortKey]) * (chartSortDir === "asc" ? 1 : -1)) : data;
  const ChTh: React.FC<{ k: string; num?: boolean; children: React.ReactNode }> = ({ k, num, children }) => (
    <th className={`sortable ${num ? "num" : ""}`} onClick={() => handleChartSort(k)} aria-sort={chartSortKey === k ? (chartSortDir === "asc" ? "ascending" : "descending") : "none"}>
      {children}<SortGlyph active={chartSortKey === k} dir={chartSortDir} />
    </th>
  );

  const filteredCustomerRevenue = useMemo(() => {
    let d = [...customerRevenue].sort((a, b) => b.totalRevenueTRY - a.totalRevenueTRY);
    const min = Number(custMinRevenue); if (min > 0) d = d.filter((r) => r.totalRevenueTRY >= min);
    const max = Number(custMaxRevenue); if (max > 0) d = d.filter((r) => r.totalRevenueTRY <= max);
    if (custTopN > 0) d = d.slice(0, custTopN);
    return d;
  }, [customerRevenue, custMinRevenue, custMaxRevenue, custTopN]);
  const custTotalTRY = filteredCustomerRevenue.reduce((s, r) => s + r.totalRevenueTRY, 0);

  // Maliyet detayı: atölye bazında gruplar
  const detailGroups = useMemo(() => {
    if (!detailRow) return [];
    const map = new Map<string, ModelCost[]>();
    detailRow.costItems.forEach((c) => {
      const key = c.order?.workshop?.name || c.usage || "Genel";
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(c);
    });
    return Array.from(map.entries()).map(([name, costs]) => ({ name, costs, total: costs.reduce((s, c) => s + costToTRY(c.totalCost, c), 0) }));
  }, [detailRow]);

  /* ---------- Parçalar ---------- */
  const thumbCell = (row: ReportRow, cls: string) => {
    const t = thumbs[row.orderId];
    const open = () => setImageOrderId(row.orderId);
    if (t) return <img src={getImageUrl(t)} alt="" className={cls} onClick={open} title="Görselleri yönet" />;
    if (t === null) return <button type="button" className={`${cls} none`} onClick={open} title="Görsel yok — eklemek için tıklayın">{I.image}</button>;
    return <span className={`${cls} wait`} aria-hidden="true" />;
  };

  const technicsCell = (row: ReportRow) => {
    if (row.technics.length === 0) return <span className="fr-muted">-</span>;
    const open = expandedTechnics.has(row.orderId);
    const shown = open || row.technics.length <= 3 ? row.technics : row.technics.slice(0, 2);
    const toggle = () => setExpandedTechnics((prev) => { const n = new Set(prev); n.has(row.orderId) ? n.delete(row.orderId) : n.add(row.orderId); return n; });
    return (
      <div className="fr-tags">
        {shown.map((t, i) => <span key={i} className="fr-tag">{t}</span>)}
        {row.technics.length > 3 && (
          <button type="button" className="fr-tag more" onClick={toggle}>{open ? "Gizle" : `+${row.technics.length - 2}`}</button>
        )}
      </div>
    );
  };

  const rowActions = (row: ReportRow, labels = false) => (
    <div className="fr-actions">
      <button type="button" className={labels ? "fr-btn small" : "fr-ib"} onClick={() => setDetailRow(row)} title="Maliyet detayları">
        {I.eye}{labels && "Maliyet Detayları"}
      </button>
      {row.modelId && (
        <button type="button" className={labels ? "fr-btn small" : "fr-ib"} onClick={() => setOrderCosts({ orderId: row.orderId, modelId: row.modelId })} title="Sipariş maliyetleri">
          {I.coin}{labels && "Sipariş Maliyetleri"}
        </button>
      )}
    </div>
  );

  const Th: React.FC<{ field?: string; cls?: string; children: React.ReactNode }> = ({ field, cls = "", children }) =>
    field ? (
      <th className={`sortable ${cls}`} onClick={() => handleSort(field)} aria-sort={sortField === field ? (sortDirection === "asc" ? "ascending" : "descending") : "none"}>
        {children}<SortGlyph active={sortField === field} dir={sortDirection} />
      </th>
    ) : <th className={cls}>{children}</th>;

  if (!booted) return <PageLoader message="Mali raporlar yükleniyor..." />;

  const isTable = activeTab === "table";
  const kpiTone = totals.profit >= 0 ? "ok" : "err";

  /* ========================================================================== */
  return (
    <div className="fr-page">
      <header className="fr-head">
        <div>
          <span className="fr-kicker">Finans</span>
          <h1>Mali Raporlar</h1>
          <p>Sipariş bazında tutar, maliyet ve kâr; aylık adet, ciro ve kâr analizleri. Tüm tutarlar o günkü kurla TL'ye çevrilir.</p>
        </div>
        {isTable && (
          <div className="fr-head-actions">
            <button type="button" className="fr-btn" onClick={loadAllOrders} disabled={isLoadingAll || !hasMore} title={hasMore ? "Kalan tüm sayfaları tek seferde getirir" : "Tüm kayıtlar zaten yüklü"}>
              {isLoadingAll ? <span className="fr-spin" aria-hidden="true" /> : I.layers}
              {isLoadingAll ? "Yükleniyor…" : hasMore ? "Tüm Siparişleri Yükle" : "Tümü Yüklendi"}
            </button>
            {has("reports:export") && (
              <button type="button" className="fr-btn primary" onClick={handleExportExcel} disabled={filteredRows.length === 0}>
                {I.download}Excel İndir
              </button>
            )}
          </div>
        )}
      </header>

      <nav className="fr-tabs" aria-label="Rapor sekmeleri">
        {TABS.map((t) => (
          <button key={t.key} type="button" className={activeTab === t.key ? "on" : ""} onClick={() => setActiveTab(t.key)} aria-current={activeTab === t.key ? "page" : undefined}>
            {t.icon}{t.label}
          </button>
        ))}
      </nav>

      {/* ======================= SİPARİŞ TABLOSU ======================= */}
      {isTable && (
        <>
          <section className="fr-card fr-filters">
            <div className="fr-toolbar">
              <label className="fr-field">
                <span>Firma</span>
                <FirmPickerField variant="toolbar" value={selectedFirmId === "all" ? "" : selectedFirmId} firms={firms} nullLabel="Tüm Firmalar" allowCreate={false} title="Firmaya göre süz" onChange={(f) => setSelectedFirmId(f?.firmId || "all")} />
              </label>

              <div className="fr-field">
                <span>Model</span>
                <div className={`fr-combo ${modelOpen ? "open" : ""} ${selectedModel ? "has" : ""}`} ref={comboRef}>
                  <button type="button" className="fr-combo-btn" onClick={() => setModelOpen((o) => !o)} aria-haspopup="listbox" aria-expanded={modelOpen}>
                    <span>{selectedModel ? selectedModel.modelCode : "Tüm Modeller"}</span>{I.chevron}
                  </button>
                  {modelOpen && (
                    <div className="fr-combo-pop">
                      <div className="fr-combo-search">{I.search}<input autoFocus placeholder="Model kodu veya adı…" value={modelSearch} onChange={(e) => setModelSearch(e.target.value)} /></div>
                      <div className="fr-combo-list" role="listbox">
                        <button type="button" role="option" aria-selected={selectedModelId === "all"} className={`fr-combo-opt ${selectedModelId === "all" ? "on" : ""}`} onClick={() => pickModel("all")}>Tüm Modeller</button>
                        {visibleModels.map((m) => (
                          <button key={m.modelId} type="button" role="option" aria-selected={selectedModelId === m.modelId} className={`fr-combo-opt ${selectedModelId === m.modelId ? "on" : ""}`} onClick={() => pickModel(m.modelId)}>
                            <b>{m.modelCode}</b>{m.modelName && <small>{m.modelName}</small>}
                          </button>
                        ))}
                        {visibleModels.length === 0 && <div className="fr-combo-none">Eşleşen model yok</div>}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <label className="fr-field">
                <span>Başlangıç</span>
                <input type="date" className="fr-input" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
              </label>
              <label className="fr-field">
                <span>Bitiş</span>
                <input type="date" className="fr-input" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
              </label>

              <div className="fr-field">
                <span>Boya adedi</span>
                <div className={`fr-range ${paintMin !== null || paintMax !== null ? "on" : ""}`}>
                  <input type="number" min={0} step={1} inputMode="numeric" placeholder="Min" value={paintCountMin} onChange={(e) => setPaintCountMin(e.target.value)} title="En az bu kadar boya çeşidi" />
                  <i>–</i>
                  <input type="number" min={0} step={1} inputMode="numeric" placeholder="Max" value={paintCountMax} onChange={(e) => setPaintCountMax(e.target.value)} title="En fazla bu kadar boya çeşidi" />
                  {(paintMin !== null || paintMax !== null) && (
                    <button type="button" onClick={() => { setPaintCountMin(""); setPaintCountMax(""); }} aria-label="Boya adedi süzgecini temizle">{I.x}</button>
                  )}
                </div>
              </div>

              <div className="fr-field">
                <span>Teknikler</span>
                <button type="button" className={`fr-input fr-techbtn ${selectedTechnics.length > 0 ? "on" : ""}`} onClick={() => setShowTechnicModal(true)}>
                  {I.layers}
                  <em>{selectedTechnics.length === 0 ? "Teknik seç" : `${selectedTechnics.length} teknik seçili`}</em>
                  {I.chevron}
                </button>
              </div>

              <label className="fr-field">
                <span>Kâr durumu</span>
                <select className="fr-input fr-select" value={profitStatusFilter} onChange={(e) => setProfitStatusFilter(e.target.value as "all" | ProfitStatus)}>
                  <option value="all">Tümü</option>
                  <option value="above">Hedef üstü (≥ %{profitMargin})</option>
                  <option value="below">Hedef altı (&lt; %{profitMargin})</option>
                  <option value="loss">Zarar</option>
                </select>
              </label>

              {activeFilterCount > 0 && (
                <button type="button" className="fr-btn ghost fr-clear" onClick={clearFilters} title="Tüm süzgeçleri temizle">
                  {I.filterOff}Temizle<span>{activeFilterCount}</span>
                </button>
              )}
            </div>

            {selectedTechnics.length > 0 && (
              <div className="fr-techbar">
                <div className="fr-techbar-modes">
                  <span className="fr-techbar-label">Eşleşme</span>
                  <div className="fr-chipgroup" role="radiogroup" aria-label="Teknik eşleşme modu">
                    {TECHNIC_MATCH_MODES.map((mode) => (
                      <button key={mode.value} type="button" role="radio" aria-checked={technicMatchMode === mode.value} className={technicMatchMode === mode.value ? "on" : ""} onClick={() => setTechnicMatchMode(mode.value)} title={mode.hint}>
                        {mode.label}
                      </button>
                    ))}
                  </div>
                  <span className="fr-hint">{getTechnicMatchHint(technicMatchMode)}</span>
                </div>
                <div className="fr-chips">
                  {selectedTechnics.map((t) => (
                    <span key={t.technicId} className="fr-chip">
                      {t.name}
                      <button type="button" onClick={() => toggleTechnic(t)} aria-label={`${t.name} süzgecini kaldır`}>{I.x}</button>
                    </span>
                  ))}
                  <button type="button" className="fr-chips-clear" onClick={() => setSelectedTechnics([])}>Tümünü temizle</button>
                </div>
              </div>
            )}
          </section>

          <div className={`fr-kpis ${loading ? "dim" : ""}`}>
            <Kpi tone="navy" label="Toplam Tutar" value={fmtTL(totals.amount)} sub={<><b>{fmtInt(filteredRows.length)}</b> kayıt · <b>{formatNumber(totals.quantity)}</b> adet</>} />
            <Kpi label="Toplam Maliyet (Genel Gider Dahil)" value={fmtTL(totals.costWithOverhead)} sub={<>Ham maliyet {fmtTL(totals.cost)} · genel gider %{overheadRate}</>} />
            <Kpi label="Hesaplanan Tutar (Kâr Dahil)" value={fmtTL(totals.finalCost)} sub={<>Hedef kâr marjı %{profitMargin}</>} />
            <Kpi tone={kpiTone} label="Kâr / Zarar" value={fmtTL(totals.profit)} sub="Toplam tutar − genel gider dahil maliyet" />
            <Kpi tone={kpiTone} label="Kâr / Zarar Oranı" value={totals.costWithOverhead > 0 ? fmtPct(totals.rate, 2) : "—"} sub={totals.costWithOverhead > 0 ? (totals.profit < 0 ? "Zarar" : totals.rate >= profitMargin ? `Hedefin (%${profitMargin}) üstünde` : `Hedefin (%${profitMargin}) altında`) : "Maliyet verisi yok"} />
          </div>

          <section className={`fr-card ${loading ? "dim" : ""}`} aria-busy={loading}>
            <div className="fr-card-head">
              <div>
                <b>{fmtInt(filteredRows.length)}</b> kayıt · <b>{formatNumber(totals.quantity)}</b> adet
                <em className="fr-muted"> · {fmtInt(rows.length)} satır yüklü{hasMore ? ", kaydırınca devamı gelir" : ", tümü"}</em>
              </div>
              <ul className="fr-stripe-legend" aria-label="Satır rengi">
                <li><i className="above" />Hedef üstü</li>
                <li><i className="below" />Hedef altı</li>
                <li><i className="loss" />Zarar</li>
              </ul>
            </div>

            {filteredRows.length === 0 ? (
              <div className="fr-empty">{I.info}<b>Gösterilecek kayıt yok</b><span>{hasFrontendFilters ? "İstemci süzgeçleri yüklü satırların tümünü eledi; süzgeçleri gevşetin veya tüm siparişleri yükleyin." : "Seçili firma, model ve tarih aralığında sipariş bulunamadı."}</span></div>
            ) : (
              <>
                {/* Masaüstü tablo */}
                <div className="fr-table-wrap fr-desktop">
                  <table className="fr-table rt">
                    <thead>
                      <tr>
                        <th className="fr-stripe" aria-label="Kâr durumu" />
                        <th className="rt-p4 fr-thumb-th">Resim</th>
                        <th className="rt-p4">ID</th>
                        <Th field="firmName">Firma</Th>
                        <Th field="modelCode">Model</Th>
                        <Th field="quantity" cls="num">Adet</Th>
                        <th className="rt-p4">Teknikler</th>
                        <Th field="paintCount" cls="num rt-p4">Boya</Th>
                        <Th field="totalAmount" cls="num">Toplam Tutar</Th>
                        <Th field="totalCost" cls="num">Maliyet</Th>
                        <Th field="costWithOverhead" cls="num rt-p3">Genel Gider (+%{overheadRate})</Th>
                        <Th field="finalCostWithProfit" cls="num rt-p3">Hesaplanan Tutar</Th>
                        <Th field="unitPrice" cls="num rt-p2">Birim Fiyat</Th>
                        <Th field="unitCost" cls="num rt-p2">Birim Maliyet</Th>
                        <Th field="suggestedPrice" cls="num rt-p3">Hesaplanan Fiyat</Th>
                        <Th field="profit" cls="num">Kâr / Zarar</Th>
                        <th className="act">İşlem</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredRows.map((row) => {
                        const st = profitStatus(row);
                        const p = profitOf(row);
                        return (
                          <tr key={row.orderId} className={st}>
                            <td className="fr-stripe" title={STATUS_LABEL[st]} />
                            <td className="rt-p4 fr-thumb-td">{thumbCell(row, "fr-thumb")}</td>
                            <td className="rt-p4"><CopyIdButton value={row.orderId} /></td>
                            <td><span className="fr-firm">{row.firmName}</span>{row.firmCode !== "-" && <small className="fr-sub">{row.firmCode}</small>}</td>
                            <td><span className="fr-model">{row.modelCode}</span><small className="fr-sub">{row.completionDate ? `Tamamlandı ${fmtDate(row.completionDate)}` : `Kabul ${fmtDate(row.acceptanceDate)}`}</small></td>
                            <td className="num"><span className="fr-qty">{formatNumber(row.quantity)}</span></td>
                            <td className="rt-p4">{technicsCell(row)}</td>
                            <td className="num rt-p4">{row.paintCount > 0 ? <span className="fr-paint">{row.paintCount}</span> : <span className="fr-muted">-</span>}</td>
                            <td className="num"><span className="fr-money strong">{fmtTL(row.totalAmount)}</span></td>
                            <td className="num"><span className="fr-money">{fmtTL(row.totalCost)}</span></td>
                            <td className="num rt-p3"><span className="fr-money accent">{fmtTL(row.costWithOverhead)}</span></td>
                            <td className="num rt-p3"><span className="fr-money accent">{fmtTL(row.finalCostWithProfit)}</span></td>
                            <td className="num rt-p2">
                              <span className="fr-money">{formatCurrency(row.unitPrice, row.currency)}</span>
                              {!isTRY(row.currency) && <small className="fr-sub">≈ {fmtTL(row.unitPriceTRY)}</small>}
                            </td>
                            <td className="num rt-p2"><span className="fr-money">{fmtTL(row.unitCost)}</span></td>
                            <td className="num rt-p3"><span className="fr-money">{fmtTL(row.suggestedPrice)}</span></td>
                            <td className="num">
                              <span className={`fr-profit ${p >= 0 ? "pos" : "neg"}`}>{fmtTL(p)}</span>
                              <span className={`fr-rate ${st}`}>{fmtPct(profitRate(row))}</span>
                            </td>
                            <td className="act">{rowActions(row)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td className="fr-stripe" />
                        <td className="rt-p4" />
                        <td className="rt-p4" />
                        <td>Toplam</td>
                        <td />
                        <td className="num">{formatNumber(totals.quantity)}</td>
                        <td className="rt-p4" />
                        <td className="rt-p4" />
                        <td className="num">{fmtTL(totals.amount)}</td>
                        <td className="num">{fmtTL(totals.cost)}</td>
                        <td className="num rt-p3">{fmtTL(totals.costWithOverhead)}</td>
                        <td className="num rt-p3">{fmtTL(totals.finalCost)}</td>
                        <td className="rt-p2" />
                        <td className="rt-p2" />
                        <td className="rt-p3" />
                        <td className="num"><span className={`fr-profit ${totals.profit >= 0 ? "pos" : "neg"}`}>{fmtTL(totals.profit)}</span></td>
                        <td />
                      </tr>
                    </tfoot>
                  </table>
                </div>

                {/* Mobil kartlar */}
                <div className="fr-mcards fr-mobile">
                  {filteredRows.map((row) => {
                    const st = profitStatus(row);
                    const p = profitOf(row);
                    return (
                      <article key={row.orderId} className={`fr-mcard ${st}`}>
                        <header className="fr-mcard-head">
                          {thumbCell(row, "fr-thumb")}
                          <div className="fr-mcard-title">
                            <span className="fr-firm">{row.firmName}</span>
                            <span className="fr-model">{row.modelCode}</span>
                            <small className="fr-sub">{row.completionDate ? `Tamamlandı ${fmtDate(row.completionDate)}` : `Kabul ${fmtDate(row.acceptanceDate)}`}</small>
                          </div>
                          <span className={`fr-rate ${st}`}>{fmtPct(profitRate(row))}</span>
                        </header>
                        <dl className="fr-mcard-body">
                          <dt>Sipariş No</dt><dd><CopyIdButton value={row.orderId} /></dd>
                          <dt>Adet</dt><dd><span className="fr-qty">{formatNumber(row.quantity)}</span></dd>
                          {row.technics.length > 0 && <><dt>Teknikler</dt><dd>{technicsCell(row)}</dd></>}
                          {row.paintCount > 0 && <><dt>Boya adedi</dt><dd><span className="fr-paint">{row.paintCount}</span></dd></>}
                          <dt>Toplam tutar</dt><dd className="fr-money strong">{fmtTL(row.totalAmount)}</dd>
                          <dt>Maliyet</dt><dd className="fr-money">{fmtTL(row.totalCost)}</dd>
                          <dt>Genel gider dahil</dt><dd className="fr-money accent">{fmtTL(row.costWithOverhead)}</dd>
                          <dt>Hesaplanan tutar</dt><dd className="fr-money accent">{fmtTL(row.finalCostWithProfit)}</dd>
                          <dt>Birim fiyat</dt><dd className="fr-money">{formatCurrency(row.unitPrice, row.currency)}{!isTRY(row.currency) && <small className="fr-sub">≈ {fmtTL(row.unitPriceTRY)}</small>}</dd>
                          <dt>Birim maliyet</dt><dd className="fr-money">{fmtTL(row.unitCost)}</dd>
                          <dt>Hesaplanan fiyat</dt><dd className="fr-money">{fmtTL(row.suggestedPrice)}</dd>
                          <dt>Kâr / zarar</dt><dd><span className={`fr-profit ${p >= 0 ? "pos" : "neg"}`}>{fmtTL(p)}</span></dd>
                        </dl>
                        <footer className="fr-mcard-foot">{rowActions(row, true)}</footer>
                      </article>
                    );
                  })}
                  <section className="fr-mtotal">
                    <h3>Genel Toplam</h3>
                    <dl>
                      <dt>Toplam adet</dt><dd>{formatNumber(totals.quantity)}</dd>
                      <dt>Toplam tutar</dt><dd className="strong">{fmtTL(totals.amount)}</dd>
                      <dt>Toplam maliyet</dt><dd>{fmtTL(totals.cost)}</dd>
                      <dt>Genel gider dahil</dt><dd className="accent">{fmtTL(totals.costWithOverhead)}</dd>
                      <dt>Hesaplanan tutar</dt><dd className="accent">{fmtTL(totals.finalCost)}</dd>
                      <dt>Kâr / zarar</dt><dd><span className={`fr-profit ${totals.profit >= 0 ? "pos" : "neg"}`}>{fmtTL(totals.profit)}</span></dd>
                    </dl>
                  </section>
                </div>
              </>
            )}

            {isLoadingMore && <div className="fr-foot"><span className="fr-spin" aria-hidden="true" />Daha fazla kayıt yükleniyor…</div>}
            {!isLoadingMore && !hasMore && rows.length > 0 && <div className="fr-foot done">{I.check}Tüm kayıtlar yüklendi</div>}
          </section>
        </>
      )}

      {/* ======================= GRAFİK SEKMELERİ ======================= */}
      {!isTable && (
        <>
          <section className="fr-card fr-filters">
            <div className="fr-toolbar">
              {activeTab !== "customer-revenue" ? (
                <>
                  <label className="fr-field">
                    <span>Yıl</span>
                    <select className="fr-input fr-select" value={chartYear} onChange={(e) => setChartYear(Number(e.target.value))}>
                      {availableYears.map((y) => <option key={y} value={y}>{y}</option>)}
                    </select>
                  </label>
                  <label className="fr-field">
                    <span>Firma</span>
                    <FirmPickerField variant="toolbar" value={selectedFirmId === "all" ? "" : selectedFirmId} firms={firms} nullLabel="Tüm Firmalar" allowCreate={false} title="Firmaya göre süz" onChange={(f) => setSelectedFirmId(f?.firmId || "all")} />
                  </label>
                  {activeTab === "monthly-revenue" && (
                    <label className="fr-field fr-switch-field">
                      <span>Gösterim</span>
                      <span className="fr-switch">
                        <input type="checkbox" checked={revenueConvertTRY} onChange={(e) => setRevenueConvertTRY(e.target.checked)} />
                        <i />
                        <em>Dövizi TL'ye çevir</em>
                      </span>
                    </label>
                  )}
                </>
              ) : (
                <>
                  <label className="fr-field">
                    <span>Yıl</span>
                    <select className="fr-input fr-select" value={custYear} onChange={(e) => { const v = e.target.value; setCustYear(v === "" ? "" : Number(v)); setStartDate(""); setEndDate(""); }}>
                      {availableYears.map((y) => <option key={y} value={y}>{y}</option>)}
                      <option value="">Tümü</option>
                    </select>
                  </label>
                  <label className="fr-field">
                    <span>Başlangıç</span>
                    <input type="date" className="fr-input" value={startDate} onChange={(e) => { setStartDate(e.target.value); if (e.target.value) setCustYear(""); }} />
                  </label>
                  <label className="fr-field">
                    <span>Bitiş</span>
                    <input type="date" className="fr-input" value={endDate} onChange={(e) => { setEndDate(e.target.value); if (e.target.value) setCustYear(""); }} />
                  </label>
                  <label className="fr-field">
                    <span>Min. ciro (₺)</span>
                    <input type="number" className="fr-input narrow" placeholder="0" value={custMinRevenue} onChange={(e) => setCustMinRevenue(e.target.value)} />
                  </label>
                  <label className="fr-field">
                    <span>Maks. ciro (₺)</span>
                    <input type="number" className="fr-input narrow" placeholder="0" value={custMaxRevenue} onChange={(e) => setCustMaxRevenue(e.target.value)} />
                  </label>
                  <label className="fr-field">
                    <span>İlk N</span>
                    <select className="fr-input fr-select narrow" value={custTopN} onChange={(e) => setCustTopN(Number(e.target.value))}>
                      <option value={0}>Tümü</option>
                      <option value={5}>İlk 5</option>
                      <option value={10}>İlk 10</option>
                      <option value={20}>İlk 20</option>
                    </select>
                  </label>
                </>
              )}
              {chartLoading && <span className="fr-loading-pill"><span className="fr-spin" aria-hidden="true" />Güncelleniyor…</span>}
            </div>
          </section>

          <div className={`fr-charts ${chartLoading ? "dim" : ""}`} aria-busy={chartLoading}>
            {/* ---------- AYLIK ADET ---------- */}
            {activeTab === "monthly-quantity" && (() => {
              const d = monthlyQuantity;
              const sum = (k: keyof MonthlyQuantityDto) => d.reduce((s, r) => s + (r[k] as number), 0);
              return (
                <>
                  <div className="fr-kpis">
                    <Kpi tone="navy" label="Toplam Sipariş" value={fmtInt(sum("orderCount"))} sub={`${chartYear} · ${d.length} ay`} />
                    <Kpi label="Toplam Miktar" value={formatNumber(sum("totalQuantity"))} />
                    <Kpi label="Toplam Parça" value={fmtInt(sum("totalPieceCount"))} />
                  </div>
                  <div className="fr-tiles three">
                    <Tile title="Sipariş adedi" sub={`Aylık sipariş sayısı — ${chartYear}`}>
                      <ResponsiveContainer width="100%" height={240}>
                        <BarChart data={d} margin={{ top: 8, right: 8, left: 0, bottom: 0 }} barCategoryGap="30%">
                          <CartesianGrid vertical={false} stroke={CH.grid} />
                          <XAxis dataKey="monthName" tickLine={false} axisLine={{ stroke: CH.axis }} tick={TICK} interval={0} tickFormatter={(v) => String(v).slice(0, 3)} />
                          <YAxis tickLine={false} axisLine={false} tick={TICK} width={44} tickFormatter={fmtAxis} allowDecimals={false} />
                          <Tooltip cursor={{ fill: CH.cursor }} content={<FrTip fmt={(v) => fmtInt(v)} />} />
                          <Bar dataKey="orderCount" name="Sipariş adedi" fill={S.s1} radius={[4, 4, 0, 0]} maxBarSize={28} />
                        </BarChart>
                      </ResponsiveContainer>
                    </Tile>
                    <Tile title="Toplam miktar" sub={`Aylık toplam sipariş miktarı — ${chartYear}`}>
                      <ResponsiveContainer width="100%" height={240}>
                        <LineChart data={d} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                          <CartesianGrid vertical={false} stroke={CH.grid} />
                          <XAxis dataKey="monthName" tickLine={false} axisLine={{ stroke: CH.axis }} tick={TICK} interval={0} tickFormatter={(v) => String(v).slice(0, 3)} />
                          <YAxis tickLine={false} axisLine={false} tick={TICK} width={48} tickFormatter={fmtAxis} />
                          <Tooltip cursor={{ stroke: CH.axis }} content={<FrTip fmt={(v) => formatNumber(v)} />} />
                          <Line type="monotone" dataKey="totalQuantity" name="Toplam miktar" stroke={S.s2} strokeWidth={2} dot={DOT(S.s2)} activeDot={{ r: 5 }} />
                        </LineChart>
                      </ResponsiveContainer>
                    </Tile>
                    <Tile title="Parça sayısı" sub={`Aylık toplam parça — ${chartYear}`}>
                      <ResponsiveContainer width="100%" height={240}>
                        <BarChart data={d} margin={{ top: 8, right: 8, left: 0, bottom: 0 }} barCategoryGap="30%">
                          <CartesianGrid vertical={false} stroke={CH.grid} />
                          <XAxis dataKey="monthName" tickLine={false} axisLine={{ stroke: CH.axis }} tick={TICK} interval={0} tickFormatter={(v) => String(v).slice(0, 3)} />
                          <YAxis tickLine={false} axisLine={false} tick={TICK} width={48} tickFormatter={fmtAxis} allowDecimals={false} />
                          <Tooltip cursor={{ fill: CH.cursor }} content={<FrTip fmt={(v) => fmtInt(v)} />} />
                          <Bar dataKey="totalPieceCount" name="Parça sayısı" fill={S.s3} radius={[4, 4, 0, 0]} maxBarSize={28} />
                        </BarChart>
                      </ResponsiveContainer>
                    </Tile>
                  </div>
                  <section className="fr-card">
                    <div className="fr-card-head"><div><b>Detay tablosu</b> · {chartYear}</div></div>
                    <div className="fr-table-wrap">
                      <table className="fr-table small">
                        <thead><tr><ChTh k="monthName">Ay</ChTh><ChTh k="orderCount" num>Sipariş adedi</ChTh><ChTh k="totalQuantity" num>Toplam miktar</ChTh><ChTh k="totalPieceCount" num>Parça sayısı</ChTh></tr></thead>
                        <tbody>
                          {sortedData(d).map((r) => (
                            <tr key={`${r.year}-${r.month}`}><td>{r.monthName}</td><td className="num">{fmtInt(r.orderCount)}</td><td className="num">{formatNumber(r.totalQuantity)}</td><td className="num">{fmtInt(r.totalPieceCount)}</td></tr>
                          ))}
                          {d.length === 0 && <tr><td colSpan={4} className="fr-none">Veri bulunamadı</td></tr>}
                        </tbody>
                        {d.length > 0 && <tfoot><tr><td>Toplam</td><td className="num">{fmtInt(sum("orderCount"))}</td><td className="num">{formatNumber(sum("totalQuantity"))}</td><td className="num">{fmtInt(sum("totalPieceCount"))}</td></tr></tfoot>}
                      </table>
                    </div>
                  </section>
                </>
              );
            })()}

            {/* ---------- AYLIK CİRO ---------- */}
            {activeTab === "monthly-revenue" && (() => {
              const d = monthlyRevenue;
              const sum = (k: keyof MonthlyRevenueDto) => d.reduce((s, r) => s + (r[k] as number), 0);
              const xAxis = <XAxis dataKey="monthName" tickLine={false} axisLine={{ stroke: CH.axis }} tick={TICK} interval={0} tickFormatter={(v) => String(v).slice(0, 3)} />;
              const currencyTile = (key: keyof MonthlyRevenueDto, cur: string, color: string, title: string) => (
                <Tile title={title} sub={`${cur} kesilen siparişler, orijinal para birimiyle — ${chartYear}`}>
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={d} margin={{ top: 8, right: 8, left: 0, bottom: 0 }} barCategoryGap="30%">
                      <CartesianGrid vertical={false} stroke={CH.grid} />
                      {xAxis}
                      <YAxis tickLine={false} axisLine={false} tick={TICK} width={52} tickFormatter={fmtAxis} />
                      <Tooltip cursor={{ fill: CH.cursor }} content={<FrTip fmt={(v) => formatCurrency(v, cur)} />} />
                      <Bar dataKey={key} name={`${cur} ciro`} fill={color} radius={[4, 4, 0, 0]} maxBarSize={28} />
                    </BarChart>
                  </ResponsiveContainer>
                </Tile>
              );
              return (
                <>
                  <div className="fr-kpis">
                    <Kpi tone="navy" label="Toplam Ciro (TL)" value={fmtTL(sum("totalRevenueTRY"))} sub={`${chartYear} · dövizler o günkü kurla`} />
                    {!revenueConvertTRY && <Kpi label="USD Ciro" value={formatCurrency(sum("revenueUSD"), "USD")} sub="Orijinal para birimi" />}
                    {!revenueConvertTRY && <Kpi label="EUR Ciro" value={formatCurrency(sum("revenueEUR"), "EUR")} sub="Orijinal para birimi" />}
                    <Kpi label="Toplam Sipariş" value={fmtInt(sum("orderCount"))} sub={`${formatNumber(sum("totalQuantity"))} adet`} />
                  </div>
                  {revenueConvertTRY ? (
                    <div className="fr-tiles two">
                      <Tile title="Aylık toplam ciro (TL)" sub={`Tüm para birimleri TL'ye çevrilmiş — ${chartYear}`}>
                        <ResponsiveContainer width="100%" height={260}>
                          <BarChart data={d} margin={{ top: 8, right: 8, left: 0, bottom: 0 }} barCategoryGap="30%">
                            <CartesianGrid vertical={false} stroke={CH.grid} />
                            {xAxis}
                            <YAxis tickLine={false} axisLine={false} tick={TICK} width={56} tickFormatter={fmtAxis} />
                            <Tooltip cursor={{ fill: CH.cursor }} content={<FrTip fmt={(v) => fmtTL(v)} />} />
                            <Bar dataKey="totalRevenueTRY" name="Toplam ciro (TL)" fill={S.s1} radius={[4, 4, 0, 0]} maxBarSize={30} />
                          </BarChart>
                        </ResponsiveContainer>
                      </Tile>
                      <Tile title="Ciro trendi (TL)" sub={`Aylık toplam ciro eğilimi — ${chartYear}`}>
                        <ResponsiveContainer width="100%" height={260}>
                          <AreaChart data={d} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                            <CartesianGrid vertical={false} stroke={CH.grid} />
                            {xAxis}
                            <YAxis tickLine={false} axisLine={false} tick={TICK} width={56} tickFormatter={fmtAxis} />
                            <Tooltip cursor={{ stroke: CH.axis }} content={<FrTip fmt={(v) => fmtTL(v)} />} />
                            <Area type="monotone" dataKey="totalRevenueTRY" name="Toplam ciro (TL)" stroke={S.s1} strokeWidth={2} fill={S.s1} fillOpacity={0.08} dot={DOT(S.s1)} activeDot={{ r: 5 }} />
                          </AreaChart>
                        </ResponsiveContainer>
                      </Tile>
                    </div>
                  ) : (
                    <>
                      <div className="fr-tiles three">
                        {currencyTile("revenueTRY", "TRY", S.s1, "TL ciro")}
                        {currencyTile("revenueUSD", "USD", S.s2, "USD ciro")}
                        {currencyTile("revenueEUR", "EUR", S.s3, "EUR ciro")}
                      </div>
                      <div className="fr-tiles">
                        <Tile title="Toplam ciro trendi (TL)" sub={`Tüm para birimleri o günkü kurla TL'ye çevrilmiş — ${chartYear}`}>
                          <ResponsiveContainer width="100%" height={240}>
                            <AreaChart data={d} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                              <CartesianGrid vertical={false} stroke={CH.grid} />
                              {xAxis}
                              <YAxis tickLine={false} axisLine={false} tick={TICK} width={56} tickFormatter={fmtAxis} />
                              <Tooltip cursor={{ stroke: CH.axis }} content={<FrTip fmt={(v) => fmtTL(v)} />} />
                              <Area type="monotone" dataKey="totalRevenueTRY" name="Toplam ciro (TL)" stroke={S.s1} strokeWidth={2} fill={S.s1} fillOpacity={0.08} dot={DOT(S.s1)} activeDot={{ r: 5 }} />
                            </AreaChart>
                          </ResponsiveContainer>
                        </Tile>
                      </div>
                    </>
                  )}
                  <section className="fr-card">
                    <div className="fr-card-head"><div><b>Detay tablosu</b> · {chartYear}</div></div>
                    <div className="fr-table-wrap">
                      <table className="fr-table small">
                        <thead>
                          <tr>
                            <ChTh k="monthName">Ay</ChTh><ChTh k="orderCount" num>Sipariş</ChTh><ChTh k="totalQuantity" num>Miktar</ChTh>
                            {!revenueConvertTRY && <><ChTh k="revenueTRY" num>TL ciro</ChTh><ChTh k="revenueUSD" num>USD ciro</ChTh><ChTh k="revenueEUR" num>EUR ciro</ChTh></>}
                            <ChTh k="totalRevenueTRY" num>Toplam (TL)</ChTh>
                          </tr>
                        </thead>
                        <tbody>
                          {sortedData(d).map((r) => (
                            <tr key={`${r.year}-${r.month}`}>
                              <td>{r.monthName}</td><td className="num">{fmtInt(r.orderCount)}</td><td className="num">{formatNumber(r.totalQuantity)}</td>
                              {!revenueConvertTRY && <><td className="num">{formatCurrency(r.revenueTRY, "TRY")}</td><td className="num">{formatCurrency(r.revenueUSD, "USD")}</td><td className="num">{formatCurrency(r.revenueEUR, "EUR")}</td></>}
                              <td className="num strong">{fmtTL(r.totalRevenueTRY)}</td>
                            </tr>
                          ))}
                          {d.length === 0 && <tr><td colSpan={revenueConvertTRY ? 4 : 7} className="fr-none">Veri bulunamadı</td></tr>}
                        </tbody>
                        {d.length > 0 && (
                          <tfoot><tr>
                            <td>Toplam</td><td className="num">{fmtInt(sum("orderCount"))}</td><td className="num">{formatNumber(sum("totalQuantity"))}</td>
                            {!revenueConvertTRY && <><td className="num">{formatCurrency(sum("revenueTRY"), "TRY")}</td><td className="num">{formatCurrency(sum("revenueUSD"), "USD")}</td><td className="num">{formatCurrency(sum("revenueEUR"), "EUR")}</td></>}
                            <td className="num">{fmtTL(sum("totalRevenueTRY"))}</td>
                          </tr></tfoot>
                        )}
                      </table>
                    </div>
                  </section>
                </>
              );
            })()}

            {/* ---------- MÜŞTERİ CİRO ---------- */}
            {activeTab === "customer-revenue" && (() => {
              const d = filteredCustomerRevenue;
              const sum = (k: keyof CustomerRevenueDto) => d.reduce((s, r) => s + (r[k] as number), 0);
              const period = startDate || endDate ? `${startDate ? fmtDate(startDate) : "…"} – ${endDate ? fmtDate(endDate) : "…"}` : custYear ? String(custYear) : "Tüm zamanlar";
              const h = Math.max(220, d.length * 34 + 40);
              return (
                <>
                  <div className="fr-kpis">
                    <Kpi tone="navy" label="Toplam Ciro (TL)" value={fmtTL(custTotalTRY)} sub={period} />
                    <Kpi label="USD Ciro" value={formatCurrency(sum("revenueUSD"), "USD")} sub="Orijinal para birimi" />
                    <Kpi label="EUR Ciro" value={formatCurrency(sum("revenueEUR"), "EUR")} sub="Orijinal para birimi" />
                    <Kpi label="Toplam Sipariş" value={fmtInt(sum("orderCount"))} sub={`${formatNumber(sum("totalQuantity"))} adet`} />
                    <Kpi label="Müşteri" value={d.length} sub={customerRevenue.length !== d.length ? `${customerRevenue.length} müşteri içinden süzüldü` : "Dönemde sipariş veren"} />
                  </div>
                  <div className="fr-tiles">
                    <Tile title="Müşteri bazlı ciro (TL)" sub={`Ciroya göre sıralı — ${period}`}>
                      {d.length === 0 ? <div className="fr-none pad">Veri bulunamadı</div> : (
                        <ResponsiveContainer width="100%" height={h}>
                          <BarChart data={d} layout="vertical" margin={{ top: 4, right: 72, left: 8, bottom: 0 }} barCategoryGap="28%">
                            <CartesianGrid horizontal={false} stroke={CH.grid} />
                            <XAxis type="number" tickLine={false} axisLine={{ stroke: CH.axis }} tick={TICK} tickFormatter={fmtAxis} />
                            <YAxis type="category" dataKey="firmName" tickLine={false} axisLine={false} tick={TICK} width={150} interval={0} tickFormatter={(v) => trunc(String(v || "-"), 22)} />
                            <Tooltip cursor={{ fill: CH.cursor }} content={<FrTip fmt={(v) => fmtTL(v)} />} />
                            <Bar dataKey="totalRevenueTRY" name="Toplam ciro (TL)" fill={S.s1} radius={[0, 4, 4, 0]} maxBarSize={22}>
                              {d.length <= 25 && <LabelList dataKey="totalRevenueTRY" position="right" fill={CH.tick} fontSize={11} formatter={(v: unknown) => fmtAxis(v)} />}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      )}
                    </Tile>
                  </div>
                  <section className="fr-card">
                    <div className="fr-card-head"><div><b>Detay tablosu</b>{customerRevenue.length !== d.length && <> · {d.length} / {customerRevenue.length} müşteri</>}</div><em className="fr-muted">Pay, listelenen müşterilerin toplam TL cirosuna göredir</em></div>
                    <div className="fr-table-wrap">
                      <table className="fr-table small">
                        <thead>
                          <tr>
                            <ChTh k="firmName">Firma</ChTh><ChTh k="orderCount" num>Sipariş</ChTh><ChTh k="totalQuantity" num>Miktar</ChTh><ChTh k="totalPieceCount" num>Parça</ChTh>
                            <ChTh k="revenueTRY" num>TL ciro</ChTh><ChTh k="revenueUSD" num>USD ciro</ChTh><ChTh k="revenueEUR" num>EUR ciro</ChTh><ChTh k="totalRevenueTRY" num>Toplam (TL)</ChTh><th className="num">Pay</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sortedData(d).map((r) => {
                            const share = custTotalTRY > 0 ? (r.totalRevenueTRY / custTotalTRY) * 100 : 0;
                            return (
                              <tr key={r.firmId}>
                                <td><span className="fr-firm">{r.firmName || "-"}</span>{r.firmCode && <small className="fr-sub">{r.firmCode}</small>}</td>
                                <td className="num">{fmtInt(r.orderCount)}</td><td className="num">{formatNumber(r.totalQuantity)}</td><td className="num">{fmtInt(r.totalPieceCount)}</td>
                                <td className="num">{formatCurrency(r.revenueTRY, "TRY")}</td><td className="num">{formatCurrency(r.revenueUSD, "USD")}</td><td className="num">{formatCurrency(r.revenueEUR, "EUR")}</td>
                                <td className="num strong">{fmtTL(r.totalRevenueTRY)}</td>
                                <td className="num"><span className="fr-share"><i style={{ width: `${Math.max(2, share)}%` }} /><b>{fmtPct(share)}</b></span></td>
                              </tr>
                            );
                          })}
                          {d.length === 0 && <tr><td colSpan={9} className="fr-none">Veri bulunamadı</td></tr>}
                        </tbody>
                        {d.length > 0 && (
                          <tfoot><tr>
                            <td>Toplam</td><td className="num">{fmtInt(sum("orderCount"))}</td><td className="num">{formatNumber(sum("totalQuantity"))}</td><td className="num">{fmtInt(sum("totalPieceCount"))}</td>
                            <td className="num">{formatCurrency(sum("revenueTRY"), "TRY")}</td><td className="num">{formatCurrency(sum("revenueUSD"), "USD")}</td><td className="num">{formatCurrency(sum("revenueEUR"), "EUR")}</td>
                            <td className="num">{fmtTL(custTotalTRY)}</td><td className="num">%100</td>
                          </tr></tfoot>
                        )}
                      </table>
                    </div>
                  </section>
                </>
              );
            })()}

            {/* ---------- AYLIK KÂR ---------- */}
            {activeTab === "monthly-profit" && (() => {
              const d = monthlyProfit;
              const sum = (k: keyof MonthlyProfitDto) => d.reduce((s, r) => s + (r[k] as number), 0);
              const rev = sum("totalRevenueTRY"); const costOH = sum("totalCostWithOverhead"); const net = sum("netProfit");
              const margin = costOH > 0 ? ((rev - costOH) / costOH) * 100 : null;
              const xAxis = <XAxis dataKey="monthName" tickLine={false} axisLine={{ stroke: CH.axis }} tick={TICK} interval={0} tickFormatter={(v) => String(v).slice(0, 3)} />;
              return (
                <>
                  <div className="fr-kpis">
                    <Kpi tone="navy" label="Toplam Ciro" value={fmtTL(rev)} sub={`${chartYear} · ${fmtInt(sum("orderCount"))} sipariş`} />
                    <Kpi label="Toplam Maliyet" value={fmtTL(costOH)} sub={`Genel gider dahil · ham ${fmtTL(sum("totalCostTRY"))}`} />
                    <Kpi tone={net >= 0 ? "ok" : "err"} label="Net Kâr" value={fmtTL(net)} sub={`Brüt kâr ${fmtTL(sum("grossProfit"))}`} />
                    <Kpi tone={margin === null ? "soft" : margin >= 0 ? "ok" : "err"} label="Kâr Marjı" value={margin === null ? "—" : fmtPct(margin)} sub={`Hedef %${profitMargin}`} />
                  </div>
                  <div className="fr-tiles two">
                    <Tile title="Ciro ve maliyet" sub={`Aylık ciro ile genel gider dahil maliyet — ${chartYear}`} legend={[{ name: "Ciro", color: S.s1 }, { name: "Maliyet (G.G. dahil)", color: S.s2 }]}>
                      <ResponsiveContainer width="100%" height={260}>
                        <BarChart data={d} margin={{ top: 8, right: 8, left: 0, bottom: 0 }} barCategoryGap="28%" barGap={2}>
                          <CartesianGrid vertical={false} stroke={CH.grid} />
                          {xAxis}
                          <YAxis tickLine={false} axisLine={false} tick={TICK} width={56} tickFormatter={fmtAxis} />
                          <Tooltip cursor={{ fill: CH.cursor }} content={<FrTip fmt={(v) => fmtTL(v)} />} />
                          <Bar dataKey="totalRevenueTRY" name="Ciro" fill={S.s1} radius={[4, 4, 0, 0]} maxBarSize={22} />
                          <Bar dataKey="totalCostWithOverhead" name="Maliyet (G.G. dahil)" fill={S.s2} radius={[4, 4, 0, 0]} maxBarSize={22} />
                        </BarChart>
                      </ResponsiveContainer>
                    </Tile>
                    <Tile title="Kâr marjı" sub={`Aylık kâr marjı, % — hedef çizgisi %${profitMargin}`}>
                      <ResponsiveContainer width="100%" height={260}>
                        <LineChart data={d} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                          <CartesianGrid vertical={false} stroke={CH.grid} />
                          {xAxis}
                          <YAxis tickLine={false} axisLine={false} tick={TICK} width={44} tickFormatter={(v) => `%${v}`} />
                          <Tooltip cursor={{ stroke: CH.axis }} content={<FrTip fmt={(v) => fmtPct(v)} />} />
                          <ReferenceLine y={0} stroke={CH.axis} />
                          <ReferenceLine y={profitMargin} stroke={CH.tick} strokeDasharray="4 4" label={{ value: `Hedef %${profitMargin}`, position: "insideTopRight", fill: CH.tick, fontSize: 11 }} />
                          <Line type="monotone" dataKey="profitMarginPercent" name="Kâr marjı" stroke={S.s3} strokeWidth={2} dot={DOT(S.s3)} activeDot={{ r: 5 }} />
                        </LineChart>
                      </ResponsiveContainer>
                    </Tile>
                  </div>
                  <div className="fr-tiles">
                    <Tile title="Brüt ve net kâr" sub={`Aylık kâr eğilimi, TL — ${chartYear}`} legend={[{ name: "Brüt kâr", color: S.s4 }, { name: "Net kâr", color: S.s3 }]}>
                      <ResponsiveContainer width="100%" height={240}>
                        <AreaChart data={d} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                          <CartesianGrid vertical={false} stroke={CH.grid} />
                          {xAxis}
                          <YAxis tickLine={false} axisLine={false} tick={TICK} width={56} tickFormatter={fmtAxis} />
                          <Tooltip cursor={{ stroke: CH.axis }} content={<FrTip fmt={(v) => fmtTL(v)} />} />
                          <ReferenceLine y={0} stroke={CH.axis} />
                          <Area type="monotone" dataKey="grossProfit" name="Brüt kâr" stroke={S.s4} strokeWidth={2} fill={S.s4} fillOpacity={0.06} dot={DOT(S.s4)} activeDot={{ r: 5 }} />
                          <Area type="monotone" dataKey="netProfit" name="Net kâr" stroke={S.s3} strokeWidth={2} fill={S.s3} fillOpacity={0.1} dot={DOT(S.s3)} activeDot={{ r: 5 }} />
                        </AreaChart>
                      </ResponsiveContainer>
                    </Tile>
                  </div>
                  <section className="fr-card">
                    <div className="fr-card-head"><div><b>Detay tablosu</b> · {chartYear}</div></div>
                    <div className="fr-table-wrap">
                      <table className="fr-table small">
                        <thead>
                          <tr>
                            <ChTh k="monthName">Ay</ChTh><ChTh k="orderCount" num>Sipariş</ChTh><ChTh k="totalRevenueTRY" num>Ciro</ChTh><ChTh k="totalCostTRY" num>Maliyet</ChTh>
                            <ChTh k="totalCostWithOverhead" num>G.G. dahil</ChTh><ChTh k="grossProfit" num>Brüt kâr</ChTh><ChTh k="netProfit" num>Net kâr</ChTh><ChTh k="profitMarginPercent" num>Marj</ChTh>
                          </tr>
                        </thead>
                        <tbody>
                          {sortedData(d).map((r) => (
                            <tr key={`${r.year}-${r.month}`}>
                              <td>{r.monthName}</td><td className="num">{fmtInt(r.orderCount)}</td><td className="num">{fmtTL(r.totalRevenueTRY)}</td><td className="num">{fmtTL(r.totalCostTRY)}</td><td className="num">{fmtTL(r.totalCostWithOverhead)}</td>
                              <td className="num"><span className={`fr-profit ${r.grossProfit >= 0 ? "pos" : "neg"}`}>{fmtTL(r.grossProfit)}</span></td>
                              <td className="num"><span className={`fr-profit ${r.netProfit >= 0 ? "pos" : "neg"}`}>{fmtTL(r.netProfit)}</span></td>
                              <td className="num"><span className={`fr-rate ${r.profitMarginPercent < 0 ? "loss" : r.profitMarginPercent < profitMargin ? "below" : "above"}`}>{fmtPct(r.profitMarginPercent)}</span></td>
                            </tr>
                          ))}
                          {d.length === 0 && <tr><td colSpan={8} className="fr-none">Veri bulunamadı</td></tr>}
                        </tbody>
                        {d.length > 0 && (
                          <tfoot><tr>
                            <td>Toplam</td><td className="num">{fmtInt(sum("orderCount"))}</td><td className="num">{fmtTL(rev)}</td><td className="num">{fmtTL(sum("totalCostTRY"))}</td><td className="num">{fmtTL(costOH)}</td>
                            <td className="num"><span className={`fr-profit ${sum("grossProfit") >= 0 ? "pos" : "neg"}`}>{fmtTL(sum("grossProfit"))}</span></td>
                            <td className="num"><span className={`fr-profit ${net >= 0 ? "pos" : "neg"}`}>{fmtTL(net)}</span></td>
                            <td className="num">{margin === null ? "—" : fmtPct(margin)}</td>
                          </tr></tfoot>
                        )}
                      </table>
                    </div>
                  </section>
                </>
              );
            })()}
          </div>
        </>
      )}

      {/* ======================= MALİYET DETAYI ======================= */}
      {detailRow && (
        <div className="fr-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) setDetailRow(null); }}>
          <div className="fr-modal wide" role="dialog" aria-modal="true" aria-labelledby="fr-detail-title">
            <header className="fr-modal-head">
              <div>
                <span className="fr-kicker">Maliyet detayları</span>
                <h3 id="fr-detail-title">{detailRow.firmName}</h3>
                <div className="fr-meta">
                  <span className="fr-badge navy">Model {detailRow.modelCode}</span>
                  <span className="fr-badge muted">{formatNumber(detailRow.quantity)} adet</span>
                  {detailRow.completionDate && <span className="fr-badge muted">Tamamlandı {fmtDate(detailRow.completionDate)}</span>}
                  <CopyIdButton value={detailRow.orderId} />
                </div>
              </div>
              <button type="button" className="fr-ib big" onClick={() => setDetailRow(null)} aria-label="Kapat">{I.x}</button>
            </header>

            <div className="fr-modal-body">
              {detailGroups.length === 0 ? (
                <div className="fr-empty">{I.info}<b>Maliyet kalemi yok</b><span>Bu sipariş için kayıtlı maliyet kalemi bulunamadı.</span></div>
              ) : detailGroups.map((g) => (
                <section key={g.name} className="fr-group">
                  <header className="fr-group-head">
                    <div className="fr-group-title">{I.workshop}<span>{g.name}</span><small>{g.costs.length} kalem</small></div>
                    <b>{fmtTL(g.total)}</b>
                  </header>
                  <div className="fr-table-wrap">
                    <table className="fr-table small">
                      <thead>
                        <tr><th>Maliyet kalemi</th><th>Kategori</th><th className="num">Miktar</th><th>Birim</th><th className="num">Fire</th><th className="num">Birim fiyat</th><th className="num">Toplam</th><th className="num">TL karşılığı</th><th>Not</th></tr>
                      </thead>
                      <tbody>
                        {g.costs.map((c) => (
                          <tr key={c.modelCostId}>
                            <td><b>{c.costItemName || "-"}</b></td>
                            <td>{c.costCategoryName ? <span className="fr-badge muted">{c.costCategoryName}</span> : "-"}</td>
                            <td className="num">{formatNumber(c.quantity)}{c.quantity2 ? ` × ${formatNumber(c.quantity2)}` : ""}</td>
                            <td>{c.unit || "-"}{c.unit2 ? ` × ${c.unit2}` : ""}</td>
                            <td className="num">{c.wastagePercentage ? `%${c.wastagePercentage}` : "-"}</td>
                            <td className="num">{formatCurrency(c.unitPrice || 0, c.currency || "TRY")}</td>
                            <td className="num strong">{formatCurrency(c.totalCost || 0, c.currency || "TRY")}</td>
                            <td className="num">
                              {fmtTL(costToTRY(c.totalCost, c))}
                              {!isTRY(c.currency) && (c.usdRate || c.eurRate || c.gbpRate) && (
                                <small className="fr-sub">Kur {(c.currency === "USD" ? c.usdRate : c.currency === "EUR" ? c.eurRate : c.gbpRate)?.toFixed(4)} TL</small>
                              )}
                            </td>
                            <td className="fr-note">{c.notes ? <em>{c.notes}</em> : <span className="fr-muted">-</span>}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              ))}

              <div className="fr-grand">
                <div className="fr-grand-row"><span>Genel toplam</span><b>{fmtTL(detailRow.totalCost)}</b></div>
                <div className="fr-grand-row accent"><span>Genel gider dahil <small>(+%{overheadRate})</small></span><b>{fmtTL(detailRow.costWithOverhead)}</b></div>
                <div className="fr-grand-row final"><span>Kâr dahil — nihai maliyet <small>(+%{profitMargin})</small></span><b>{fmtTL(detailRow.finalCostWithProfit)}</b></div>
                {detailRow.quantity > 0 && <div className="fr-grand-row unit"><span>Birim maliyet <small>(genel gider dahil ÷ adet)</small></span><b>{fmtTL(detailRow.costWithOverhead / detailRow.quantity)}</b></div>}
              </div>
            </div>

            <footer className="fr-modal-foot">
              {detailRow.modelId && <button type="button" className="fr-btn" onClick={() => setOrderCosts({ orderId: detailRow.orderId, modelId: detailRow.modelId })}>{I.coin}Sipariş Maliyetleri</button>}
              <button type="button" className="fr-btn primary" onClick={() => setDetailRow(null)}>Kapat</button>
            </footer>
          </div>
        </div>
      )}

      {/* ======================= DİĞER PENCERELER ======================= */}
      <TechnicModal isOpen={showTechnicModal} onClose={() => setShowTechnicModal(false)} onSelectTechnic={toggleTechnic} selectedTechnics={selectedTechnics} onClearAll={() => setSelectedTechnics([])} />
      <OrderCostsModal isOpen={!!orderCosts} orderId={orderCosts?.orderId || ""} modelId={orderCosts?.modelId || ""} onClose={() => setOrderCosts(null)} />
      <AlertModal isOpen={alert.isOpen} message={alert.message} type={alert.type} onClose={() => setAlert((a) => ({ ...a, isOpen: false }))} />
      {imageOrderId && <ImageManager orderId={imageOrderId} onClose={() => setImageOrderId(null)} />}
    </div>
  );
};

export default Reports;
